import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useMe } from '../../lib/me';
import { useFeature } from '../../lib/features';
import { contractorAuth, contractorIdentity } from '../../lib/contractorAuth';
import TicketEditPanel from '../TicketEditPanel';
import CustomFieldsForm from '../CustomFieldsForm';
import HandBack from '../HandBack';
import TicketDialog from './TicketDialog';
import DetailsPanel from './DetailsPanel';
import RequestPanel, { StepsOpenDialog } from '../catalogue/RequestPanel';
import ShortcutsSheet from './ShortcutsSheet';
import { neighbours } from '../../lib/ticketOrder';
import {
  roleOf, abilitiesOf, tabsFor, tabFromPath, stepsFor, statusOf, isSettled,
  slaChip, statusTone, priorityTone, isSentBack, openQuestion, stamp
} from './ticketView';
import './Ticket.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Which tab the address is on this moment. The router's own copy
// (location) is updated in a transition, so on a slow machine a tab just
// asked for (g w, Back) may not be drawn yet: r then focused the reply box
// still on screen, and the Work & costs tab replaced it a moment later.
const onConversation = () => tabFromPath(window.location.pathname) === 'conversation';

/**
 * ONE TICKET PAGE FOR EVERY ROLE (October 2026 redesign, approved by Gordon).
 *
 * A sticky header - number, title, status, priority, the SLA while its clock
 * runs, the people, a stage stepper and the one or two things this person
 * does next - over a row of tabs that are real addresses:
 *
 *   /tickets/:id            Conversation      /tickets/:id/docs     Docs & to-dos
 *   /tickets/:id/work       Work & costs      /tickets/:id/files    Files
 *   /tickets/:id/device     Device            /tickets/:id/history  History
 *
 * (and the same under /my-tickets/:id for a contractor). Each tab loads its
 * own data when it is opened; this shell loads the ticket, a one-request
 * summary for the counts, and the SLA. On a desktop the Details panel sits
 * beside the tab; narrower, it is a Details tab of its own.
 *
 * Who sees what is ticketView.js. Every write is still the server's to allow.
 */
export default function TicketLayout({ mode = 'staff' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const todosOn = useFeature('todos');
  // The Device tab goes with the Asset Portal (wave 7); the server sends no
  // device card to somebody the switch leaves out.
  const assetsOn = useFeature('assets');
  const contractorMode = mode === 'contractor';
  const contractor = contractorMode ? (contractorIdentity() || {}) : null;
  // Staff requests go through the app's interceptor; a contractor's carry
  // their portal token, which the interceptor does not attach.
  const auth = useMemo(() => (contractorMode ? contractorAuth() : undefined), [contractorMode]);
  const base = contractorMode ? `/my-tickets/${id}` : `/tickets/${id}`;

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sla, setSla] = useState(null);
  const [notice, setNotice] = useState(null);
  const [problem, setProblem] = useState(null);
  const [busy, setBusy] = useState(null);
  const [dialog, setDialog] = useState(null);       // 'edit' | 'fields' | 'handback' | 'reopen' | 'keys'
  const [menuOpen, setMenuOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [editField, setEditField] = useState(null); // asked for by a shortcut: 'status' | 'assigned_to'
  const [reopens, setReopens] = useState([]);       // a contractor's asks to reopen a settled job
  const [answering, setAnswering] = useState(false); // the requester's "Reply and resubmit" box (087)
  const [resolveCheck, setResolveCheck] = useState(null); // "Marco hasn't started this yet" (resolve-guard)
  const [stepsCheck, setStepsCheck] = useState(null);     // a catalogue request's checklist still open (124)
  const [requestRev, setRequestRev] = useState(0);        // the request panel reloads after a resolve
  const composer = useRef(null);
  const arrival = useRef(location.state || null);
  // Where in the browser's history this ticket was opened, so "← Tickets"
  // goes back to the list it came from however many tabs were opened since -
  // the tabs are history entries of their own, which is what makes Back
  // between them work.
  const entry = useRef(typeof window !== 'undefined' ? (window.history.state?.idx ?? null) : null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setLoadError(null);
      const r = await axios.get(`${API_BASE}/tickets/${id}`, auth);
      setTicket(r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : null);
      if (!r.data || Array.isArray(r.data)) setLoadError({ status: 404 });
    } catch (err) {
      const status = err.response?.status || null;
      setLoadError({ status, message: err.response?.data?.error || (status ? null : 'The server could not be reached.') });
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [id, auth]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/tickets/${id}/summary`, auth);
      setSummary(r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : null);
    } catch {
      setSummary(null);
    }
  }, [id, auth]);

  useEffect(() => { load(); loadSummary(); }, [load, loadSummary]);
  // Another ticket opened in the same frame (a related ticket's link) starts
  // its own "where did I come from".
  useEffect(() => { entry.current = window.history.state?.idx ?? null; }, [id]);

  const role = ticket ? roleOf({ me, ticket, contractor: contractorMode ? contractor : null }) : null;
  const can = ticket ? abilitiesOf({ me, ticket, role }) : {};
  const seesSla = Boolean(can.seesSla);

  useEffect(() => {
    if (!seesSla || !id) { setSla(null); return undefined; }
    let live = true;
    axios.get(`${API_BASE}/tickets/${id}/sla`)
      .then((r) => { if (live) setSla(r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : null); })
      .catch(() => { if (live) setSla(null); });
    return () => { live = false; };
  }, [id, seesSla]);

  // A settled job, for the contractor: whether they have already asked for it
  // back, so the page can say so rather than offer to ask twice.
  const settledForContractor = contractorMode && Boolean(ticket) && isSettled(ticket);
  const loadReopens = useCallback(async () => {
    if (!settledForContractor) { setReopens([]); return; }
    try {
      const r = await axios.get(`${API_BASE}/tickets/${id}/reopen-requests`, auth);
      setReopens(Array.isArray(r.data) ? r.data : []);
    } catch { setReopens([]); }
  }, [settledForContractor, id, auth]);
  useEffect(() => { loadReopens(); }, [loadReopens]);

  const reload = useCallback(async () => {
    await Promise.all([load({ quiet: true }), loadSummary(), loadReopens()]);
  }, [load, loadSummary, loadReopens]);

  /* --------------------------------------------- the compact header */
  // Shrinking the header makes the page shorter, which near the foot of a
  // page pulls the scroll back under the line and grows it again - the
  // header blinked (Gordon, 26 Oct). Two guards: a gap between the line in
  // (140px) and the line out (40px), and a spacer that keeps the page the
  // same height while the header is small, so shrinking never moves the
  // scroll at all.
  const headRef = useRef(null);
  const fullHead = useRef(0);
  const [spare, setSpare] = useState(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        // Keep the full height up to date while it is full: the header grows
        // after the ticket loads, so a height taken once at the start is short.
        const head = headRef.current;
        if (head && !head.classList.contains('tk-head-compact')) fullHead.current = head.offsetHeight;
        setCompact((was) => (was ? y > 40 : y > 140));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);
  useLayoutEffect(() => {
    const el = headRef.current;
    if (!el) return;
    if (!compact) { fullHead.current = el.offsetHeight; setSpare(0); return; }
    setSpare(Math.max(0, fullHead.current - el.offsetHeight));
  }, [compact]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  /* ------------------------------------------------------- actions */
  const act = useCallback(async (key, fn, done) => {
    setBusy(key);
    setProblem(null);
    try {
      await fn();
      if (done) setNotice(done);
      await reload();
    } catch (err) {
      setProblem(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }, [reload]);

  const focusReply = useCallback(() => {
    const here = onConversation();
    if (here && composer.current) { composer.current.focus(); return; }
    // On its way to the conversation (Back), the box is not drawn yet: the
    // tab focuses it when it is.
    navigate(base, { replace: here, state: { focusReply: Date.now() } });
  }, [navigate, base]);

  /*
   * MARK RESOLVED. Somebody else's worker who has not started, or not
   * finished, is not resolved over their head without a word: the server
   * refuses it (lib/resolve-guard.js, code 'worker_not_done') and this
   * shows what it said - "Marco hasn't started this yet" - with Ask for an
   * update, Resolve anyway (a reason) and Cancel. Nothing has changed when
   * the dialog opens. The worker resolving their own job never sees it.
   */
  const markResolved = async () => {
    setBusy('resolve');
    setProblem(null);
    try {
      await axios.patch(`${API_BASE}/tickets/${id}/status`, { status: 'Resolved' }, auth);
      setNotice('Marked resolved. It goes to the approver for sign-off.');
      await reload();
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.code === 'worker_not_done') {
        setResolveCheck(err.response.data);
      } else if (err.response?.status === 400 && err.response?.data?.code === 'steps_open') {
        setStepsCheck(err.response.data);
      } else {
        setProblem(err.response?.data?.error || 'That did not go through.');
      }
    } finally {
      setBusy(null);
    }
  };
  const setStatus = (status) => act(`status:${status}`, () => axios.patch(
    `${API_BASE}/tickets/${id}/status`, { status }, auth), `Marked ${status}.`);
  const assignToMe = () => act('assign', () => axios.put(
    `${API_BASE}/tickets/${id}`, { assigned_to: me?.id }), 'Assigned to you.');
  const logTime = () => navigate(`${base}/work`, { state: { logTime: Date.now() } });
  const decide = () => {
    if (!onConversation()) navigate(base, { state: { decide: Date.now() } });
    else document.getElementById('tk-decide')?.focus();
  };
  const answer = (fixed, note = '') => act(fixed ? 'fixed' : 'broken', () => axios.post(
    `${API_BASE}/tickets/${id}/requester-answer`, { fixed, note }),
    fixed ? 'Thank you. IT will close it.' : 'IT has been told it is still broken.');
  const copyLink = async () => {
    setMenuOpen(false);
    try {
      await navigator.clipboard.writeText(window.location.href);
      setNotice('Link copied.');
    } catch {
      setNotice(window.location.href);
    }
  };

  /* ------------------------------------------------------ shortcuts */
  const tabs = role ? tabsFor(role, { todosOn, assetsOn }) : [];
  const gPending = useRef(0);
  const shortcutsOn = useRef(true);
  useEffect(() => {
    try { shortcutsOn.current = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch { /* old browser */ }
  }, []);
  useEffect(() => {
    if (!ticket) return undefined;
    const onKey = (e) => {
      if (!shortcutsOn.current || e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (typing || dialog || document.querySelector('.tk-scrim, .tds-wrap, [role="dialog"][aria-modal="true"]')) return;
      const k = e.key;
      if (Date.now() - gPending.current < 1200) {
        gPending.current = 0;
        const tab = tabs.find((x) => x.short === k);
        if (tab) { e.preventDefault(); navigate(tab.path ? `${base}/${tab.path}` : base); }
        return;
      }
      if (k === 'g') { gPending.current = Date.now(); return; }
      if ((k === '[' || k === ']') && !contractorMode) {
        const n = neighbours(id);
        const to = k === '[' ? n.prev : n.next;
        if (to) { e.preventDefault(); navigate(`/tickets/${to}`); }
        return;
      }
      if (k === '?') { e.preventDefault(); setDialog('keys'); return; }
      if (k === 'r') { e.preventDefault(); focusReply(); return; }
      if (k === 'l' && can.canWork && tabs.some((x) => x.key === 'work')) { e.preventDefault(); logTime(); return; }
      if ((k === 's' || k === 'a') && can.canEdit) {
        e.preventDefault();
        setEditField(k === 's' ? 'status_id' : 'assigned_to');
        if (window.matchMedia && window.matchMedia('(max-width: 1023px)').matches) navigate(`${base}/details`);
      }
      if (k === 'Escape') { setMenuOpen(false); setEditField(null); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  /* ------------------------------------------------------ rendering */
  if (loading && !ticket) {
    return <div className="tk-page"><p className="tk-quiet tk-loading">Loading the ticket…</p></div>;
  }
  if (!ticket) {
    const s = loadError?.status;
    return (
      <div className="tk-page">
        <div className="tk-wrap">
          <div className="tk-card tk-gone">
            <h1 className="tk-gone-title">
              {s === 404 ? (contractorMode ? 'This ticket did not open' : 'That ticket is not there')
                : s === 401 || s === 403 ? 'That ticket is not yours to open'
                  : 'This ticket could not be loaded'}
            </h1>
            <p className="tk-quiet">
              {s === 404
                ? (contractorMode ? 'That ticket is not one of yours, or no longer exists.'
                  : 'It has either been deleted, or the link points at an id that was never a ticket.')
                : loadError?.message || 'The server did not say why, which usually means it could not be reached at all.'}
            </p>
            <p className="tk-mono">{s ? `HTTP ${s}` : 'no response'} · {id}</p>
            <div className="tk-row">
              <button type="button" className="tk-btn" onClick={() => load()}>Try again</button>
              <button type="button" className="tk-btn tk-btn-quiet"
                      onClick={() => navigate(contractorMode ? '/contractor-dashboard' : '/my-tickets')}>
                {contractorMode ? '← Back to my work' : '← My tickets'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // A tab this person does not get (an old link, or a role that changed):
  // the conversation, rather than an empty frame.
  const here = tabFromPath(location.pathname);
  if (here !== 'conversation' && here !== 'details' && !tabs.some((t) => t.key === here)) {
    return <Navigate to={base} replace />;
  }

  const locked = isSettled(ticket);
  const s = statusOf(ticket);
  const plain = role === 'requester' || role === 'raiser';
  const stage = stepsFor(ticket, { plain });
  const chip = seesSla ? slaChip(sla, ticket) : null;
  const answered = summary?.answer?.answer || null;
  const waitingOnRequester = role === 'requester' && s === 'resolved' && !answered;
  // Sent back for more information (087): the approver's question, for the
  // person who raised it - the next move is theirs.
  const question = openQuestion(ticket);
  const asksMe = Boolean(question) && Boolean(can.canResubmit)
    && (role === 'requester' || role === 'raiser' || Boolean(ticket.viewer?.is_requester));
  const openAnswer = () => {
    setAnswering(true);
    setTimeout(() => document.getElementById('tk-resubmit-text')?.focus(), 30);
  };

  /* The one or two things this person does next (proposal table 3.3). */
  const primary = [];
  if (role === 'requester' || role === 'raiser') {
    if (waitingOnRequester) {
      primary.push({ key: 'fixed', label: 'Yes, it’s fixed', tone: 'go', run: () => answer(true) });
      primary.push({ key: 'broken', label: 'Still broken', run: () => navigate(base, { state: { broken: Date.now() } }) });
    } else if (asksMe) {
      primary.push({ key: 'resubmit', label: 'Reply and resubmit', tone: 'primary', run: openAnswer });
    } else {
      primary.push({ key: 'reply', label: 'Reply', tone: 'primary', run: focusReply });
    }
  } else if (can.canDecide) {
    primary.push({ key: 'decide', label: 'Decide', tone: 'primary', run: decide });
  } else if (role === 'contractor' && locked) {
    if (reopens[0]?.state !== 'pending') {
      primary.push({ key: 'reopen', label: 'Ask to reopen', tone: 'primary', run: () => setDialog('reopen') });
    }
  } else {
    if (can.canWork && tabs.some((t) => t.key === 'work') && !locked && s !== 'resolved' && s !== 'awaiting approval') {
      primary.push({ key: 'log', label: 'Log time', run: logTime });
    }
    if ((can.canWork || can.canEdit) && ['open', 'in progress', 'on hold'].includes(s)) {
      primary.push({ key: 'resolve', label: 'Mark resolved', tone: 'go', run: markResolved });
    }
    if (primary.length === 0 && role === 'admin' && me?.id && String(ticket.assigned_to || '') !== String(me.id)
        && !locked && s !== 'resolved') {
      primary.push({ key: 'assign', label: 'Assign to me', tone: 'primary', run: assignToMe });
    }
  }

  /* Everything else, in the ⋯ menu. */
  const menu = [];
  if (can.canEdit) menu.push({ key: 'edit', label: 'Edit ticket…', run: () => setDialog('edit') });
  if (can.canEdit && ticket.category_name) menu.push({ key: 'fields', label: 'Edit extra fields…', run: () => setDialog('fields') });
  if ((role === 'contractor' || role === 'worker') && !locked) {
    if (s !== 'on hold') menu.push({ key: 'hold', label: 'Put on hold', run: () => setStatus('On Hold') });
    if (s !== 'in progress') menu.push({ key: 'progress', label: 'Mark in progress', run: () => setStatus('In Progress') });
  }
  if ((role === 'contractor' || role === 'worker') && !locked) {
    menu.push({ key: 'handback', label: 'Hand back…', run: () => setDialog('handback') });
  }
  if (role === 'admin' && me?.id && String(ticket.assigned_to || '') !== String(me.id) && !primary.some((p) => p.key === 'assign') && !locked) {
    menu.push({ key: 'assign', label: 'Assign to me', run: assignToMe });
  }
  if (can.canDecide) menu.push({ key: 'queue', label: 'Open the Approvals queue', run: () => navigate('/approvals') });
  menu.push({ key: 'copy', label: 'Copy link', run: copyLink });
  menu.push({ key: 'keys', label: 'Keyboard shortcuts', hint: '?', run: () => setDialog('keys') });

  const people = [ticket.assigned_to_name, ticket.contractor_name].filter(Boolean);
  const ctx = {
    ticket, summary, sla, role, can, me, contractor, contractorMode, auth, base, id, locked,
    reload, loadSummary, setNotice, setProblem, composer, answer, busy, arrival,
    todosOn, editField, setEditField, markResolved
  };
  const count = (key) => {
    if (!summary) return null;
    if (key === 'conversation') return summary.comments?.count ?? null;
    if (key === 'files') return summary.files ?? null;
    return null;
  };
  const back = () => {
    const now = window.history.state?.idx;
    if (entry.current > 0 && Number.isInteger(now) && now >= entry.current) navigate(-(now - entry.current + 1));
    else navigate(contractorMode ? '/contractor-dashboard' : role === 'admin' ? '/tickets' : '/my-tickets');
  };

  return (
    <div className="tk-page" data-role={role}>
      <header ref={headRef} className={`tk-head${compact ? ' tk-head-compact' : ''}`}>
        <div className="tk-head-in">
          <div className="tk-crumbs">
            <button type="button" className="tk-back" onClick={back}>
              <span aria-hidden="true">←</span> {contractorMode ? 'My work' : 'Tickets'}
            </button>
            <span className="tk-sep" aria-hidden="true">/</span>
            <span className="tk-number">{ticket.ticket_number}</span>
            {!contractorMode && (() => {
              const n = neighbours(id);
              if (!n.prev && !n.next) return null;
              return (
                <span className="tk-pager" aria-label="In the list you came from">
                  <button type="button" className="tk-icon-btn tk-pager-btn" disabled={!n.prev}
                          aria-label="Previous ticket in the list" title="Previous ticket ( [ )"
                          onClick={() => navigate(`/tickets/${n.prev}`)}>‹</button>
                  <span className="tk-pager-at">{n.at + 1} of {n.total}</span>
                  <button type="button" className="tk-icon-btn tk-pager-btn" disabled={!n.next}
                          aria-label="Next ticket in the list" title="Next ticket ( ] )"
                          onClick={() => navigate(`/tickets/${n.next}`)}>›</button>
                </span>
              );
            })()}
            {locked && <span className="tk-chip" data-tone="grey">Settled — view only</span>}
            {role === 'raiser' && <span className="tk-chip" data-tone="blue">Your request</span>}
          </div>

          <div className="tk-titlerow">
            <h1 className="tk-title">{ticket.title}</h1>
            <div className="tk-actions">
              {primary.slice(0, 2).map((a) => (
                <button key={a.key} type="button"
                        className={`tk-btn${a.tone === 'primary' ? ' tk-btn-primary' : a.tone === 'go' ? ' tk-btn-go' : ''}`}
                        data-action={a.key}
                        disabled={Boolean(busy)}
                        onClick={a.run}>
                  {busy === a.key ? 'Working…' : a.label}
                </button>
              ))}
              <OverflowMenu items={menu} open={menuOpen} setOpen={setMenuOpen} />
            </div>
          </div>

          <div className="tk-chips">
            <span className="tk-chip tk-chip-status" data-tone={statusTone(ticket)}>{ticket.status_name || ticket.status}</span>
            {ticket.priority_name && (
              <span className="tk-chip" data-tone={priorityTone(ticket.priority_name)}>{ticket.priority_name}</span>
            )}
            {chip && <span className="tk-chip tk-chip-sla" data-tone={chip.tone} title={chip.title}>{chip.text}</span>}
            {isSentBack(ticket) && !plain && (
              <span className="tk-chip" data-tone="amber" data-chip="sent-back"
                    title={ticket.sendback?.question ? `Asked: ${ticket.sendback.question}` : undefined}>
                Sent back · waiting for {String(ticket.requester_name || 'the requester').split(/\s+/)[0]}
              </span>
            )}
            {ticket.requester_name && !plain && (
              <span className="tk-chip tk-chip-person" title="Raised by">
                <span className="tk-dot" aria-hidden="true">{initials(ticket.requester_name)}</span>
                {ticket.requester_name}
              </span>
            )}
            {people.length > 0 && (
              <span className="tk-chip tk-chip-person" title="Working on it">→ {people.join(' · ')}</span>
            )}
            {[ticket.department_name, ticket.category_name].filter(Boolean).length > 0 && (
              <span className="tk-chip tk-chip-quiet">{[ticket.department_name, ticket.category_name].filter(Boolean).join(' · ')}</span>
            )}
          </div>

          {stage.rejected ? (
            <p className="tk-rejected">Not approved{ticket.rejection_reason ? ` — ${ticket.rejection_reason}` : ''}</p>
          ) : (
            <ol className="tk-steps" aria-label="Where this ticket is">
              {stage.steps.map((label, i) => (
                <li key={label} data-state={i < stage.at ? 'done' : i === stage.at ? 'here' : 'later'}
                    aria-current={i === stage.at ? 'step' : undefined}>
                  <span className="tk-step-label">{i === stage.at && stage.paused ? `${label} (on hold)` : label}</span>
                </li>
              ))}
            </ol>
          )}

          <nav className="tk-tabs" aria-label="Ticket sections">
            {tabs.map((t) => {
              const n = count(t.key);
              return (
                <NavLink key={t.key} end={!t.path} to={t.path ? `${base}/${t.path}` : base}
                         className={({ isActive }) => `tk-tab${isActive ? ' is-active' : ''}`}
                         data-tab={t.key}>
                  {t.label}
                  {n ? <span className="tk-tab-count">{n}</span> : null}
                </NavLink>
              );
            })}
            <NavLink to={`${base}/details`} className={({ isActive }) => `tk-tab tk-tab-details${isActive ? ' is-active' : ''}`}
                     data-tab="details">
              Details
            </NavLink>
          </nav>
        </div>
      </header>
      {compact && spare > 0 && <div className="tk-head-spare" aria-hidden="true" style={{ height: spare }} />}

      <div className="tk-wrap">
        {arrival.current?.notice && <p className="tk-banner" data-tone="amber" role="alert">{arrival.current.notice}</p>}
        {arrival.current?.sent && (
          <p className="tk-banner tk-sent" data-tone="blue" role="status">
            Sent for approval. It is with {ticket.approver_name || 'the approvers'}; you will see the decision here and in your bell.
            {Array.isArray(arrival.current.filesFailed) && arrival.current.filesFailed.length > 0 && (
              <span>
                {' '}{arrival.current.filesFailed.length} file{arrival.current.filesFailed.length === 1 ? '' : 's'} did
                not upload ({arrival.current.filesFailed.join('; ')}) — add them again under Files.
              </span>
            )}
          </p>
        )}
        {settledForContractor && (
          <div className="tk-banner tk-locked" data-tone="grey">
            <p>
              The hours on this job have been signed off{s === 'closed' ? ' and the ticket is closed' : ''}, so
              nothing here can be changed. An approver has to agree before it can be reopened.
            </p>
            {reopens[0]?.state === 'pending' && (
              <p className="tk-locked-ask">You have asked for this to be reopened — it is with an approver: “{reopens[0].reason}”</p>
            )}
            {reopens[0]?.state === 'declined' && (
              <p className="tk-locked-ask">
                Your last request was declined{reopens[0].decided_by_name ? ` by ${reopens[0].decided_by_name}` : ''}
                {reopens[0].decision_note ? `: “${reopens[0].decision_note}”` : '.'} You can ask again.
              </p>
            )}
          </div>
        )}
        {asksMe && (
          <SentBackBanner question={question} id={id} auth={auth} open={answering} setOpen={setAnswering}
                          onOpen={openAnswer}
                          onDone={(who) => { setAnswering(false); setNotice(`Sent back to ${who} for a decision.`); reload(); }} />
        )}
        <div className="tk-live" role="status" aria-live="polite">{notice}</div>
        {problem && <p className="tk-banner" data-tone="red" role="alert">{problem}</p>}

        {/* A ticket raised from the catalogue (124): what was asked for, and its checklist. */}
        <RequestPanel key={requestRev} ticketId={id} me={me} auth={auth} contractor={contractorMode} onChanged={reload} />

        <div className="tk-grid">
          <main className="tk-main" id="tk-main">
            {here === 'details'
              ? <DetailsPanel ctx={ctx} asTab />
              : <Outlet context={ctx} />}
          </main>
          <aside className="tk-side" aria-label="Details">
            <DetailsPanel ctx={ctx} />
          </aside>
        </div>
      </div>

      {dialog === 'edit' && (
        <TicketDialog label="Edit ticket" wide onClose={() => setDialog(null)}>
          <TicketEditPanel ticket={ticket} onCancel={() => setDialog(null)}
                           onSaved={() => { setDialog(null); setNotice('Saved.'); reload(); }} />
        </TicketDialog>
      )}
      {dialog === 'fields' && (
        <TicketDialog label="Extra fields" onClose={() => setDialog(null)}>
          <CustomFieldsForm ticketId={id} ticketCategory={ticket.category_name} onValuesChanged={() => {}} />
          <div className="tk-row tk-row-end">
            <button type="button" className="tk-btn tk-btn-primary" onClick={() => { setDialog(null); reload(); }}>Done</button>
          </div>
        </TicketDialog>
      )}
      {dialog === 'handback' && (
        <TicketDialog label="Hand this ticket back" onClose={() => setDialog(null)}>
          <p className="tk-quiet">It goes back to the office with your reason, and leaves your list.</p>
          <HandBack ticket={{ ...ticket, status: ticket.status_name || ticket.status }}
                    onDone={() => { setDialog(null); navigate(contractorMode ? '/contractor-dashboard' : '/my-tickets'); }} />
        </TicketDialog>
      )}
      {dialog === 'reopen' && (
        <ReopenDialog id={id} auth={auth} onClose={() => setDialog(null)}
                      onDone={() => { setDialog(null); setNotice('Sent to the approver.'); reload(); }} />
      )}
      {dialog === 'keys' && <ShortcutsSheet onClose={() => setDialog(null)} tabs={tabs} can={can} contractor={contractorMode} />}
      {resolveCheck && (
        <ResolveCheckDialog check={resolveCheck} id={id} auth={auth} onClose={() => setResolveCheck(null)}
                            onDone={(words) => { setResolveCheck(null); setNotice(words); reload(); }} />
      )}
      {stepsCheck && (
        <StepsOpenDialog check={stepsCheck} id={id} auth={auth} onClose={() => setStepsCheck(null)}
                         onDone={(words) => { setStepsCheck(null); setNotice(words); setRequestRev((n) => n + 1); reload(); }} />
      )}
    </div>
  );
}

const initials = (n) => String(n || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/** The ⋯ menu: a button and a list of actions, arrow keys to move, Escape to close. */
function OverflowMenu({ items, open, setOpen }) {
  const wrap = useRef(null);
  const list = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const first = list.current?.querySelector('[role="menuitem"]');
    first?.focus();
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open, setOpen]);
  const onKey = (e) => {
    const els = [...(list.current?.querySelectorAll('[role="menuitem"]') || [])];
    const at = els.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); wrap.current?.querySelector('.tk-more')?.focus(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); els[(at + 1) % els.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); els[(at - 1 + els.length) % els.length]?.focus(); }
    if (e.key === 'Tab') setOpen(false);
  };
  return (
    <div className="tk-menu-wrap" ref={wrap}>
      <button type="button" className="tk-btn tk-more" aria-haspopup="menu" aria-expanded={open}
              aria-label="More actions" onClick={() => setOpen(!open)}>
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="currentColor">
          <circle cx="3" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="13" cy="8" r="1.5" />
        </svg>
      </button>
      {open && (
        <ul className="tk-menu" role="menu" ref={list} onKeyDown={onKey}>
          {items.map((it) => (
            <li key={it.key} role="none">
              <button type="button" role="menuitem" className="tk-menu-item" data-action={it.key}
                      onClick={() => { setOpen(false); it.run(); }}>
                <span>{it.label}</span>
                {it.hint && <kbd className="tk-kbd">{it.hint}</kbd>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * "YOUR APPROVER NEEDS MORE INFORMATION" (087), for the person who raised it:
 * the question word for word, who asked and when, and Reply and resubmit -
 * which puts the answer on the conversation and the ticket back in the
 * approver's queue (POST /tickets/:id/approval/resubmit).
 */
function SentBackBanner({ question, id, auth, open, setOpen, onOpen, onDone }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const who = String(question.asked_by_name || 'your approver').split(/\s+/)[0];
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/tickets/${id}/approval/resubmit`, { answer: text.trim() }, auth);
      setText('');
      onDone(who);
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="tk-sentback" aria-labelledby="tk-sentback-h" data-sentback="">
      <div className="tk-sentback-icon" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6"
             strokeLinecap="round" strokeLinejoin="round">
          <circle cx="8" cy="8" r="6.3" /><path d="M6.3 6.2a1.8 1.8 0 113 1.4c-.7.4-1.3.8-1.3 1.6M8 11.2v.1" />
        </svg>
      </div>
      <div className="tk-sentback-body">
        <h2 className="tk-sentback-title" id="tk-sentback-h">Your approver needs more information</h2>
        <blockquote className="tk-sentback-q">{question.question}</blockquote>
        <p className="tk-sentback-by">
          {question.asked_by_name || 'Your approver'} asked{question.asked_at ? ` · ${stamp(question.asked_at)}` : ''}.
          It is waiting for your answer before it can be approved.
        </p>
        {!open ? (
          <div className="tk-row">
            <button type="button" className="tk-btn tk-btn-primary" data-action="resubmit-open" onClick={onOpen}>
              Reply and resubmit
            </button>
          </div>
        ) : (
          <form className="tk-form" onSubmit={send}>
            <label className="tk-label" htmlFor="tk-resubmit-text">Your answer for {who}</label>
            <textarea id="tk-resubmit-text" className="tk-input" rows={3} value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(e); }}
                      placeholder="Answer their question - it goes on the conversation and straight back to them." />
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="tk-row">
              <button type="submit" className="tk-btn tk-btn-primary" data-action="resubmit" disabled={busy || !text.trim()}>
                {busy ? 'Sending…' : 'Send and resubmit'}
              </button>
              <button type="button" className="tk-btn tk-btn-quiet" onClick={() => { setOpen(false); setError(null); }}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

/**
 * "MARCO HASN'T STARTED THIS YET" / "MARCO IS STILL WORKING ON THIS (3.5 h
 * logged, not yet submitted)": what the server said when Mark resolved was
 * pressed on somebody else's unfinished job, and the three ways on.
 *
 *   Ask Marco for an update   a message on the ticket, which reaches them
 *                             as every ticket message does
 *   Resolve anyway            with a short reason - kept in the History, and
 *                             Marco is told; hours he logged still go for
 *                             sign-off, as before
 *   Cancel                    nothing happens
 */
function ResolveCheckDialog({ check, id, auth, onClose, onDone }) {
  const who = String(check.worker?.name || 'The worker').split(/\s+/)[0];
  const [mode, setMode] = useState(null);   // null | 'ask' | 'resolve'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const started = check.state === 'working';
  const hours = Number(check.hours) || 0;
  const headline = started ? `${who} is still working on this` : `${who} hasn’t started this yet`;
  const choose = (m) => {
    setMode(m);
    setError(null);
    setText(m === 'ask' ? `${who}, could you give us an update on this? Where does it stand?` : '');
    setTimeout(() => document.getElementById('tk-resolve-text')?.focus(), 30);
  };
  const send = async (e) => {
    e.preventDefault();
    const words = text.trim();
    if (busy || (mode === 'resolve' ? words.length < 3 : !words)) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'ask') {
        await axios.post(`${API_BASE}/tickets/${id}/comments`, { comment: words }, auth);
        onDone(`Asked ${who} for an update. They see it on the ticket.`);
      } else {
        await axios.patch(`${API_BASE}/tickets/${id}/status`, { status: 'Resolved', override_reason: words }, auth);
        onDone(`Resolved. ${who} has been told why${hours > 0 ? ', and their hours still go for sign-off' : ''}.`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <TicketDialog label={headline} onClose={onClose} className="tk-resolve-check">
      <p className="tk-resolve-state" data-state={check.state}>
        {started
          ? `${hours > 0 ? `${Number(hours.toFixed(2))} h logged, not yet submitted. ` : ''}${who} has not said the work is finished.`
          : `It is still ${check.status || 'Open'} and nothing has been logged on it.`}
        {' '}Resolving it now closes the work before {who} has {started ? 'finished' : 'started'}.
      </p>
      {mode === null && (
        <div className="tk-resolve-choices">
          <button type="button" className="tk-btn tk-btn-primary" data-action="ask-update" onClick={() => choose('ask')}>
            Ask {who} for an update
          </button>
          <button type="button" className="tk-btn" data-action="resolve-anyway" onClick={() => choose('resolve')}>
            Resolve anyway…
          </button>
          <button type="button" className="tk-btn tk-btn-quiet" onClick={onClose}>Cancel</button>
        </div>
      )}
      {mode !== null && (
        <form className="tk-form" onSubmit={send}>
          <label className="tk-label" htmlFor="tk-resolve-text">
            {mode === 'ask' ? `Your message to ${who}` : 'Why is it being resolved now?'}
          </label>
          <textarea id="tk-resolve-text" className="tk-input" rows={3} value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={mode === 'ask' ? '' : `e.g. The requester fixed it themselves. ${who} is shown this.`} />
          <p className="tk-quiet tk-small">
            {mode === 'ask'
              ? `Posted on the ticket. ${who} sees it there, and in their messages.`
              : `Kept in the History. ${who} is told${hours > 0 ? ', and the hours they logged still go to the approver for sign-off' : ''}.`}
          </p>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="tk-row">
            <button type="submit" className={`tk-btn ${mode === 'ask' ? 'tk-btn-primary' : 'tk-btn-go'}`}
                    disabled={busy || (mode === 'resolve' ? text.trim().length < 3 : !text.trim())}>
              {busy ? 'Working…' : mode === 'ask' ? 'Send' : 'Resolve anyway'}
            </button>
            <button type="button" className="tk-btn tk-btn-quiet" onClick={() => { setMode(null); setError(null); }}>Back</button>
          </div>
        </form>
      )}
    </TicketDialog>
  );
}

/** A contractor asking for a settled job back (routes/reopen-requests). */
function ReopenDialog({ id, auth, onClose, onDone }) {
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [last, setLast] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/tickets/${id}/reopen-requests`, auth)
      .then((r) => setLast(Array.isArray(r.data) ? r.data[0] || null : null))
      .catch(() => setLast(null));
  }, [id, auth]);
  const send = async (e) => {
    e.preventDefault();
    if (!why.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/tickets/${id}/reopen-requests`, { reason: why.trim() }, auth);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <TicketDialog label="Ask an approver to reopen it" onClose={onClose}>
      <p className="tk-quiet">
        The hours on this job have been signed off, so nothing can be changed until an approver agrees.
      </p>
      {last?.state === 'pending' ? (
        <p className="tk-banner" data-tone="blue">You have already asked — it is with an approver: “{last.reason}”</p>
      ) : (
        <form onSubmit={send} className="tk-form">
          {last?.state === 'declined' && (
            <p className="tk-banner" data-tone="amber">
              Your last request was declined{last.decided_by_name ? ` by ${last.decided_by_name}` : ''}
              {last.decision_note ? `: “${last.decision_note}”` : '.'}
            </p>
          )}
          <label className="tk-label" htmlFor="tk-reopen-why">What needs changing?</label>
          <textarea id="tk-reopen-why" className="tk-input" rows={4} value={why}
                    onChange={(e) => setWhy(e.target.value)}
                    placeholder="e.g. I logged 3 hours but it was 5 — the second visit never went on." />
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="tk-row tk-row-end">
            <button type="button" className="tk-btn tk-btn-quiet" onClick={onClose}>Cancel</button>
            <button type="submit" className="tk-btn tk-btn-primary" disabled={busy || !why.trim()}>Send to the approver</button>
          </div>
        </form>
      )}
    </TicketDialog>
  );
}
